import { useState, useEffect } from 'react';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';

export function GatewayLogsSection({ api }) {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);

  const loadLogs = (p, s) => {
    setLoading(true);
    var url = '/api/gateway-logs?page=' + p + '&limit=100';
    if (s) url += '&search=' + encodeURIComponent(s);
    api.get(url).then(function(res) {
      setLogs(res.data.logs || []);
      setTotal(res.data.total || 0);
      setPages(res.data.pages || 1);
    }).catch(function() {}).finally(function() { setLoading(false); });
  };

  useEffect(function() { loadLogs(page, search); }, [page]);

  var handleSearch = function() { setPage(1); loadLogs(1, search); };

  var fmtDate = function(d) {
    if (!d) return '-';
    var dt = new Date(d);
    return dt.toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' }) + ' ' + dt.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
  };

  var resultColor = function(r) {
    if (!r) return 'text-gray-500';
    if (r.toLowerCase().indexOf('success') >= 0) return 'text-green-700 bg-green-50 border-green-200';
    if (r.toLowerCase().indexOf('error') >= 0 || r.toLowerCase().indexOf('fail') >= 0) return 'text-red-700 bg-red-50 border-red-200';
    if (r.toLowerCase().indexOf('invalid') >= 0) return 'text-amber-700 bg-amber-50 border-amber-200';
    return 'text-blue-700 bg-blue-50 border-blue-200';
  };

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-[#0A1628]">Gateway Transaction Log</h2>
        <p className="text-gray-500 text-sm">Complete log of all payment gateway events</p>
      </div>

      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input type="text" value={search} onChange={function(e) { setSearch(e.target.value); }} onKeyDown={function(e) { if (e.key === 'Enter') handleSearch(); }} placeholder="Search/Filter..." className="w-full pl-10 pr-4 py-2 border rounded-lg text-sm" />
        </div>
        <button onClick={handleSearch} className="px-4 py-2 bg-[#014DB7] text-white rounded-lg text-sm font-medium">Search</button>
      </div>

      <div className="flex items-center justify-between text-sm text-gray-500">
        <span>{total} Records Found, Page {page} of {pages}</span>
        <div className="flex items-center gap-2">
          <span>Jump to Page:</span>
          <select value={page} onChange={function(e) { setPage(parseInt(e.target.value)); }} className="border rounded px-2 py-1 text-sm">
            {Array.from({ length: pages }, function(_, i) { return <option key={i + 1} value={i + 1}>{i + 1}</option>; })}
          </select>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="w-8 h-8 border-4 border-[#014DB7] border-t-transparent rounded-full animate-spin" /></div>
      ) : logs.length === 0 ? (
        <div className="bg-white rounded-xl border p-12 text-center text-gray-400">No gateway logs found</div>
      ) : (
        <div className="bg-white rounded-xl border overflow-hidden">
          <table className="w-full">
            <thead>
              <tr className="bg-[#2a5a9e] text-white">
                <th className="text-left px-4 py-3 text-sm font-semibold w-40">Date</th>
                <th className="text-left px-4 py-3 text-sm font-semibold w-32">Gateway</th>
                <th className="text-left px-4 py-3 text-sm font-semibold">Debug Data</th>
                <th className="text-center px-4 py-3 text-sm font-semibold w-44">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {logs.map(function(log) { return <LogRow key={log.id} log={log} fmtDate={fmtDate} resultColor={resultColor} />; })}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <button onClick={function() { if (page > 1) setPage(page - 1); }} disabled={page <= 1} className="p-2 border rounded-lg disabled:opacity-30"><ChevronLeft className="w-4 h-4" /></button>
          <span className="text-sm text-gray-600">Page {page} of {pages}</span>
          <button onClick={function() { if (page < pages) setPage(page + 1); }} disabled={page >= pages} className="p-2 border rounded-lg disabled:opacity-30"><ChevronRight className="w-4 h-4" /></button>
        </div>
      )}
    </div>
  );
}

function LogRow({ log, fmtDate, resultColor }) {
  return (
    <tr className="hover:bg-gray-50">
      <td className="px-4 py-4 text-sm text-gray-700 align-top">{fmtDate(log.date)}</td>
      <td className="px-4 py-4 text-sm font-medium text-gray-900 align-top">{log.gateway}</td>
      <td className="px-4 py-4 align-top">
        <textarea readOnly value={log.debug_data || ''} className="w-full h-28 text-xs font-mono bg-gray-50 border rounded-lg p-2 resize-none text-gray-700" />
      </td>
      <td className="px-4 py-4 text-center align-top">
        <span className={'inline-block px-3 py-1.5 rounded-lg text-sm font-semibold border ' + resultColor(log.result)}>{log.result}</span>
      </td>
    </tr>
  );
}
